import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { CookiesModalContextProvider } from "components/CookiesModalContext";
import LanguageContext from "components/LanguageContext";
import BackToTop from "components/LayoutFront/BackToTop";
import Banner from "components/LayoutFront/Banner";
import Breadcrumb from "components/LayoutFront/Breadcrumb";
import Content from "components/LayoutFront/Content";
import CookiesBanner from "components/LayoutFront/CookiesBanner";
import Footer from "components/LayoutFront/Footer";
import HelpAndShare from "components/LayoutFront/HelpAndShare";
import Navigation from "components/LayoutFront/Navigation";
import OldShare from "components/LayoutFront/OldShare";
import PnuBanner from "components/LayoutFront/PnuBanner";
import Wrapper from "components/LayoutFront/Wrapper";
import PageVersionContext from "components/PageVersionContext";
import SiteContext from "components/SiteContext";
import { DSFRTemplatesWithBanner, oldHomeTemplates, templatesWithoutOldShare } from "components/templates/templates";
import Icon from "components/templatesComponents/Icon";
import Link from "components/templatesComponents/Link";
import { useFindPages } from "hooks/page.hook";
import { useGetAllSites } from "hooks/site.hook";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import EulerianService from "services/EulerianService";
import { localeDateNow } from "utils/bannersUtils";
import t from "utils/locales/translation.json";
import { createQueryParams, getQueryParams } from "utils/urlUtils";
import LoadBot from "./LoadBot";

const pageWidth = "100%";

const Social = ({ socialNetworks = [] }) => {
  return (
    <ul className="fr-btns-group">
      {socialNetworks.map((social) => {
        const { id, ...others } = social.icon || {};
        return (
          <Link
            role="listitem"
            url={social.url}
            external
            sx={{
              fontSize: { xs: "1.5rem", lg: "1.25rem" },
              color: "var(--text-action-high-blue-france)",
              mr: { xs: 2, lg: 0 },
              backgroundImage: "none",
              "&.fr-link[target=_blank]:after": { content: "none" },
            }}
            key={social.id}
            title={social.network || null}
            aria-label={social.network || null}
          >
            <Icon {...others} />
            <p className="fr-sr-only">{social.network}</p>
          </Link>
        );
      })}
    </ul>
  );
};

Social.propTypes = {
  socialNetworks: PropTypes.arrayOf(PropTypes.shape()),
};

const LayoutFront = (props) => {
  const { children = null, bo = false } = props;

  const history = useHistory();

  const [displayBanner, setDisplayBanner] = useState(true);
  const { language, setLanguage } = useContext(LanguageContext);
  const { currentPageVersion = {}, loading } = useContext(PageVersionContext);
  const { template, pageId, page: currentPage = {} } = currentPageVersion;
  const { originalPageId, lang: currentPageLanguage = "FR" } = currentPage;
  const site = useContext(SiteContext);
  const { id: siteId, name: siteName, settings: siteSettings, mourningSettings } = site || {};
  const { selectedTemplates = "[]" } = siteSettings || {};
  const { isMourning, mourningStartDate, mourningEndDate } = mourningSettings || {};
  let { socialNetworks = [] } = siteSettings || {};
  const { breadcrumb = [] } = currentPageVersion || {};

  if (socialNetworks && !Array.isArray(socialNetworks)) {
    socialNetworks = [socialNetworks];
  }

  const { pathname: locationPathname, search } = useLocation();

  const queryParams = getQueryParams(search) || {};

  const { data: allSites = [] } = useGetAllSites();

  const antsSite = useMemo(() => allSites?.find((s) => s.name === "ANTS"), [allSites]);

  const { data: homePages = [] } = useFindPages({
    filters: [`page.lang||eq||${language}`, "page.parentId||isnull"],
  });

  const antsHomePage = useMemo(() => homePages.find((p) => p.page?.siteId === antsSite?.id), [homePages, antsSite]);
  const currentHomePage = useMemo(() => homePages.find((p) => p.page?.siteId === siteId), [homePages, siteId]);

  const { data: associatedPnuPages = [] } = useFindPages({
    siteId,
    pageId: currentPageLanguage === "FR" ? pageId : originalPageId,
    enabled: !loading,
  });

  const { host: antsHost } = antsSite || {};
  const { path: antsHomePagePath, fullPath: antsHomePageFullPath } = antsHomePage || {};
  const { path: currentHomePagePath } = currentHomePage || {};

  const { data: allPnuPages = [] } = useFindPages({
    siteId,
    filters: ["page.originalPageId||notnull"],
    perPage: currentPageLanguage === "FR" ? 1 : 200,
  });

  const isMourningMode = useMemo(
    () =>
      isMourning || (Date.parse(mourningStartDate) <= localeDateNow && Date.parse(mourningEndDate) >= localeDateNow),
    [isMourning, mourningEndDate, mourningStartDate],
  );

  const homePageLogoLink = useMemo(() => {
    if (["IMMAT", "PASSEPORT_CNI", "PERMIS"].includes(siteName)) {
      return `${antsHost}${antsHomePageFullPath || antsHomePagePath}`;
    }
    return antsHomePageFullPath || antsHomePagePath;
  }, [siteName, antsHomePageFullPath, antsHomePagePath, antsHost]);

  const displayPnuBanner = useMemo(
    () =>
      displayBanner &&
      !!associatedPnuPages.length &&
      !associatedPnuPages.some((m) => m.page?.lang === language.toUpperCase()),
    [displayBanner, associatedPnuPages, language],
  );

  const displayOldShare = useMemo(() => !template || !templatesWithoutOldShare.includes(template), [template]);
  const displayBreadcrumb = useMemo(
    () => !DSFRTemplatesWithBanner.concat(oldHomeTemplates).includes(template),
    [template],
  );

  const displayHelpAndShareBlock = useMemo(() => {
    if (selectedTemplates) {
      const selectedTemplatesList = JSON.parse(selectedTemplates);
      if (Array.isArray(selectedTemplatesList)) {
        return selectedTemplatesList.some((st) => st.key === template);
      }
    }
    return false;
  }, [selectedTemplates, template]);

  useEffect(() => {
    if (!allPnuPages.length) {
      setLanguage("FR");
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("language", "FR");
      }
      if (typeof document?.documentElement !== "undefined") {
        document.documentElement.lang = "fr";
      }
    } else {
      setLanguage(currentPageLanguage.toUpperCase());
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("language", currentPageLanguage.toUpperCase());
      }
      if (typeof document?.documentElement !== "undefined") {
        document.documentElement.lang = currentPageLanguage.toLowerCase();
      }
    }
  }, [setLanguage, currentPageLanguage, siteName, allPnuPages.length, isMourningMode]);

  useEffect(() => {
    if (typeof document?.documentElement !== "undefined") {
      if (isMourningMode) {
        document.documentElement.setAttribute("data-fr-mourning", "");
      } else {
        document.documentElement.removeAttribute("data-fr-mourning");
      }
    }
  }, [isMourningMode]);

  useEffect(() => setDisplayBanner(true), [locationPathname]);

  // Redirection vers la page associée à la langue du navigateur
  // (sauf page 404 et BO)
  useEffect(() => {
    if (
      typeof localStorage !== "undefined" &&
      associatedPnuPages.length > 0 &&
      associatedPnuPages.length <= 2 &&
      !bo &&
      Object.keys(currentPageVersion).length > 0 &&
      queryParams?.lang
    ) {
      const localStorageLang = localStorage.getItem("language");
      const getPageByLang = associatedPnuPages.find((p) => p.page?.lang === localStorageLang.toUpperCase());
      const pathname = getPageByLang?.fullPath || getPageByLang?.path || associatedPnuPages[0]?.fullPath;
      history.replace({
        pathname,
        search: `?${createQueryParams(queryParams)}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associatedPnuPages, bo]);

  useEffect(() => {
    if (currentPageVersion) {
      if (breadcrumb && breadcrumb.length > 0) {
        const pageTag = {
          page_name: breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 1].title : "accueil",
        };

        if (breadcrumb.length === 1) {
          pageTag.page_category2 = "accueil";
        }

        Object.keys(breadcrumb).forEach((key) => {
          if (key > 0 && key < breadcrumb.length - 1) {
            pageTag[`page_category${Number(key) + 1}`] = breadcrumb[key].title;
          }
        });

        if (breadcrumb.length <= 2) {
          pageTag.page_name = "accueil";
          pageTag[`page_category${breadcrumb.length}`] = breadcrumb[breadcrumb.length - 1].title;
        } else if (breadcrumb.length > 2) {
          pageTag.page_name = breadcrumb[breadcrumb.length - 1].title;
          pageTag[`page_category${breadcrumb.length}`] = pageTag.page_name;
        }

        EulerianService.sendPage(pageTag, siteName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageVersion && currentPageVersion.id]);

  return (
    <Navigation
      pageWidth={pageWidth}
      allPnuPages={allPnuPages}
      associatedPnuPages={associatedPnuPages}
      antsHomePageLink={homePageLogoLink}
      antsHomePagePath={antsHomePagePath}
      currentHomePagePath={currentHomePagePath}
      isMourning={isMourningMode}
      bo={bo}
    >
      <CookiesModalContextProvider>
        <Stack
          sx={{
            bgcolor: { xs: "transparent", lg: "componentColors.30" },
            flex: "1 0 auto",
            "& > footer": { bgcolor: "white" },
          }}
        >
          <Content pageWidth={pageWidth}>
            <Banner />
            <Wrapper template={template}>
              {displayPnuBanner && (
                <Box sx={{ px: { lg: 3 } }}>
                  <PnuBanner language={language} setDisplayBanner={setDisplayBanner} />
                </Box>
              )}
              {displayBreadcrumb && (
                <Stack
                  direction={{ lg: "row" }}
                  sx={{
                    alignItems: { xs: "center", lg: "baseline" },
                    justifyContent: "space-between",
                    px: { md: 3, lg: 0 },

                    "@media print": { display: "none" },
                  }}
                >
                  <Breadcrumb bo={bo} />
                  <Stack
                    sx={{
                      alignItems: { xs: "center", lg: "end" },
                      justifyContent: "center",
                      mx: 1,
                      mt: 1,
                      mb: displayOldShare ? 0 : 2,
                    }}
                  >
                    {displayOldShare && <OldShare />}
                  </Stack>
                </Stack>
              )}
            </Wrapper>
            <main id="main" role="main">
              {children}
            </main>
            {displayHelpAndShareBlock && <HelpAndShare template={template} />}
            <BackToTop scrollableClass="root" />
            <LoadBot />
          </Content>

          <footer id="footer">
            <div className="fr-follow">
              <div className="fr-container">
                <div className="fr-grid-row">
                  <div className="fr-col-12">
                    <div className="fr-follow__social">
                      <h2 className="fr-h5">
                        {t[language].footer.social_networks_1}
                        <br /> {t[language].footer.social_networks_2}
                      </h2>
                      <Social socialNetworks={socialNetworks} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer
              homePageLink={homePageLogoLink}
              homePageAntsLink={`${antsHost}${antsHomePageFullPath || antsHomePagePath}`}
              isMourning={isMourningMode}
            />
          </footer>
        </Stack>
        <CookiesBanner bo={bo || [true, "true"].includes(process.env.hide_banner)} />
      </CookiesModalContextProvider>
    </Navigation>
  );
};

LayoutFront.propTypes = {
  children: PropTypes.node,
  bo: PropTypes.bool,
};

export default LayoutFront;
